import {
  API,
  BlockAPI,
  BlockTool,
  BlockToolConstructorOptions,
  BlockToolData,
  ToolboxConfig,
} from "@editorjs/editorjs";

interface RatingData extends BlockToolData {
  rating: number;
  summary: string;
  leftColumn: string;
  rightColumn: string;
  isProsAndCons: boolean;
  recommendedBadge: boolean;
  powerBadge: boolean;
}

export default class Rating implements BlockTool {
  public static isReadOnlySupported = true;
  public static enableLineBreaks = true;
  public static sanitize = {
    div: true,
    br: true,
    ul: true,
    li: true,
    h1: true,
    h2: true,
    h3: true,
    h4: true,
    h5: true,
    h6: true,
  };

  private data: RatingData;
  private isReadOnly: boolean;
  private api: API;

  static get toolbox(): ToolboxConfig {
    return {
      title: "Ocena",
      // TODO: Find appropriate icon
      icon: "O",
    };
  }

  constructor({
    data,
    readOnly,
    api,
  }: BlockToolConstructorOptions<RatingData>) {
    this.data = {
      ...data,
      isProsAndCons: !!data.isProsAndCons,
    };
    this.isReadOnly = readOnly;
    this.api = api;
  }

  render(): HTMLElement {
    const container = document.createElement("div");
    container.classList.add(this.api.styles.block);
    container.style.display = "flex";
    container.style.flexDirection = "column";

    const rating = document.createElement("div");
    rating.classList.add(this.api.styles.input, "rating");
    rating.contentEditable = String(!this.isReadOnly);
    rating.innerHTML = this.data.rating ? String(this.data.rating) : "";
    rating.dataset.placeholder = "Ocena";

    const summary = document.createElement("div");
    summary.classList.add(this.api.styles.input, "summary");
    summary.contentEditable = String(!this.isReadOnly);
    summary.innerHTML = this.data.summary ?? "";
    summary.style.minHeight = "200px";
    summary.dataset.placeholder = "Podsumowanie";

    const columns = document.createElement("div");
    columns.style.display = "flex";
    columns.style.flexDirection = "row";
    columns.style.gap = "8px";
    columns.style.marginTop = "8px";

    const leftColumn = document.createElement("div");
    leftColumn.classList.add(this.api.styles.input, "leftColumn");
    leftColumn.contentEditable = String(!this.isReadOnly);
    leftColumn.innerHTML = this.data.leftColumn ?? "";
    leftColumn.style.minHeight = "120px";
    leftColumn.dataset.placeholder = "Lewa kolumna";

    const rightColumn = document.createElement("div");
    rightColumn.classList.add(this.api.styles.input, "rightColumn");
    rightColumn.contentEditable = String(!this.isReadOnly);
    rightColumn.style.marginTop = "0px";
    rightColumn.innerHTML = this.data.rightColumn ?? "";
    rightColumn.style.minHeight = "120px";
    rightColumn.dataset.placeholder = "Prawa kolumna";

    columns.appendChild(leftColumn);
    columns.appendChild(rightColumn);

    const badges = document.createElement("div");
    badges.style.display = "flex";
    badges.style.flexDirection = "row";
    badges.style.gap = "8px";
    badges.style.marginTop = "8px";
    badges.style.alignItems = "center";
    badges.innerHTML = "<u>Odznaki: </u>";

    const recommendedBadge = document.createElement("div");
    recommendedBadge.classList.add(
      this.api.styles.inlineToolButton,
      "recommendedBadge"
    );
    recommendedBadge.innerHTML = "CDA Poleca";
    recommendedBadge.classList.toggle(
      this.api.styles.settingsButtonActive,
      this.data.recommendedBadge ?? false
    );
    recommendedBadge.addEventListener("click", () => {
      if (!this.isReadOnly) {
        this.data.recommendedBadge = !this.data.recommendedBadge;

        recommendedBadge.classList.toggle(
          this.api.styles.settingsButtonActive,
          this.data.recommendedBadge ?? false
        );
      }
    });

    const powerBadge = document.createElement("div");
    powerBadge.classList.add(this.api.styles.inlineToolButton, "powerBadge");
    powerBadge.innerHTML = "CDA Power!";
    powerBadge.classList.toggle(
      this.api.styles.settingsButtonActive,
      this.data.powerBadge ?? false
    );
    powerBadge.addEventListener("click", () => {
      if (!this.isReadOnly) {
        this.data.powerBadge = !this.data.powerBadge;

        powerBadge.classList.toggle(
          this.api.styles.settingsButtonActive,
          this.data.powerBadge ?? false
        );
      }
    });

    badges.appendChild(recommendedBadge);
    badges.appendChild(powerBadge);

    container.appendChild(summary);
    container.appendChild(rating);
    container.appendChild(columns);
    container.appendChild(badges);

    return container;
  }

  renderSettings(): HTMLElement {
    const wrapper = document.createElement("div");

    const el = document.createElement("div");

    el.classList.add(this.api.styles.settingsButton);
    el.innerHTML = "+/-";
    el.title = `Plusy i minusy`;

    el.classList.toggle(
      this.api.styles.settingsButtonActive,
      this.data.isProsAndCons ?? false
    );

    wrapper.appendChild(el);

    const el2 = document.createElement("div");

    el2.classList.add(this.api.styles.settingsButton);
    el2.innerHTML = `<svg height='18px' width='18px' fill="currentColor" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:cc="http://creativecommons.org/ns#" xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#" xmlns:svg="http://www.w3.org/2000/svg" xmlns="http://www.w3.org/2000/svg" xmlns:sodipodi="http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd" xmlns:inkscape="http://www.inkscape.org/namespaces/inkscape" preserveAspectRatio="xMidYMid" viewBox="0 0 63 63" version="1.1" x="0px" y="0px"><defs><style> .cls-3 { fill: currentColor; } </style></defs><path style="" d="M 31.136719 0.01171875 C 13.961719 0.01171875 -0.01171875 13.987016 -0.01171875 31.166016 C -0.01171875 48.344016 13.961719 62.320312 31.136719 62.320312 C 48.311719 62.320312 62.283203 48.344016 62.283203 31.166016 C 62.283203 13.987016 48.311719 0.01171875 31.136719 0.01171875 z M 31.136719 3.0117188 C 46.656719 3.0117188 59.283203 15.642016 59.283203 31.166016 C 59.283203 46.690016 46.656719 59.320312 31.136719 59.320312 C 15.615719 59.320312 2.9882812 46.690016 2.9882812 31.166016 C 2.9882813 15.642016 15.615719 3.0117188 31.136719 3.0117188 z M 20.777344 19.412109 C 18.797344 19.412109 17.191406 21.019 17.191406 23 C 17.191406 24.982 18.797344 26.587891 20.777344 26.587891 C 22.759344 26.587891 24.365234 24.982 24.365234 23 C 24.365234 21.019 22.759344 19.412109 20.777344 19.412109 z M 41.400391 19.412109 C 39.420391 19.412109 37.8125 21.019 37.8125 23 C 37.8125 24.982 39.420391 26.587891 41.400391 26.587891 C 43.381391 26.587891 44.986328 24.982 44.986328 23 C 44.986328 21.019 43.381391 19.412109 41.400391 19.412109 z M 27.134766 37.40625 C 22.162766 37.32925 18.337734 41.014828 18.177734 41.173828 C 17.588734 41.754828 17.583109 42.699062 18.162109 43.289062 C 18.741109 43.879063 19.69025 43.889547 20.28125 43.310547 C 20.31225 43.280547 23.407891 40.29225 27.087891 40.40625 C 29.090891 40.45625 30.969781 41.416766 32.675781 43.259766 C 34.786781 45.540766 37.053922 46.693359 39.419922 46.693359 C 39.640922 46.693359 39.860984 46.682109 40.083984 46.662109 C 44.678984 46.247109 47.699172 41.575953 47.826172 41.376953 C 48.269172 40.679953 48.065141 39.756547 47.369141 39.310547 C 46.672141 38.863547 45.745828 39.067672 45.298828 39.763672 C 45.274828 39.800672 42.923734 43.401781 39.802734 43.675781 C 38.186734 43.813781 36.517953 42.992703 34.876953 41.220703 C 32.592953 38.752703 29.986766 37.46925 27.134766 37.40625 z " fill="currentColor" fill-rule="evenodd"></path></svg>`;
    el2.title = `Cieszy / niepokoi`;

    el2.classList.toggle(
      this.api.styles.settingsButtonActive,
      !this.data.isProsAndCons ?? false
    );

    wrapper.appendChild(el2);

    el.addEventListener("click", () => {
      this.toggleIsProsAndCons();

      el.classList.toggle(
        this.api.styles.settingsButtonActive,
        this.data.isProsAndCons ?? false
      );

      el2.classList.toggle(
        this.api.styles.settingsButtonActive,
        !this.data.isProsAndCons ?? false
      );
    });

    el2.addEventListener("click", () => {
      this.toggleIsProsAndCons();

      el.classList.toggle(
        this.api.styles.settingsButtonActive,
        this.data.isProsAndCons ?? false
      );

      el2.classList.toggle(
        this.api.styles.settingsButtonActive,
        !this.data.isProsAndCons ?? false
      );
    });

    return wrapper;
  }

  private toggleIsProsAndCons(): void {
    this.data.isProsAndCons = !this.data.isProsAndCons;

    this.api.blocks.update(
      (this.api.blocks.getBlockByIndex(
        this.api.blocks.getCurrentBlockIndex()
      ) as BlockAPI)?.id,
      this.data
    );
  }

  save(rootElement: HTMLElement): RatingData {
    const summary = rootElement.querySelector(".summary")?.innerHTML || "";
    const rating = rootElement.querySelector(".rating")?.innerHTML || "";
    const leftColumn =
      rootElement.querySelector(".leftColumn")?.innerHTML || "";
    const rightColumn =
      rootElement.querySelector(".rightColumn")?.innerHTML || "";

    return {
      ...this.data,
      summary,
      rating: parseFloat(rating),
      leftColumn,
      rightColumn,
    };
  }
}
